import { createSlice, current, type PayloadAction } from '@reduxjs/toolkit';
import { isShallowIdentical } from 'utils/isShallowIdentical';

import type { SaveAsDesignToProfileModalEnum } from '@inter-ikea-kompis/component-save-as-design-to-profile';
import {
  type SaveDesignToProfileModalEnum,
  SaveDesignToProfileStateEnum,
} from '@inter-ikea-kompis/component-save-design-to-profile';
import type { IAccountServiceUser } from '@inter-ikea-kompis/services';
import type { ISaveConfigurationsItem } from '@inter-ikea-kompis/types';

type Account = {
  loggedIn: boolean;
  visibleModal: SaveAsDesignToProfileModalEnum | SaveDesignToProfileModalEnum | null;
  saveDesignState: SaveDesignToProfileStateEnum;
  designs?: ISaveConfigurationsItem[];
  user?: IAccountServiceUser | null;
  currentDesignId?: string;
  currentDesignName?: string;
};

const initialState: Account = {
  loggedIn: false,
  visibleModal: null,
  saveDesignState: SaveDesignToProfileStateEnum.unsaved,
  designs: undefined,
  user: undefined,
  currentDesignId: undefined,
  currentDesignName: undefined,
};

export const { reducer: accountReducer, actions: accountAction } = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setUser: (state, { payload: user }: PayloadAction<Account['user']>) => {
      if (isShallowIdentical(current(state).user, user)) {
        return;
      }

      return { ...state, user };
    },
    setCurrentDesignId: (state, { payload: currentDesignId }: PayloadAction<Account['currentDesignId']>) => {
      if (isShallowIdentical(current(state).currentDesignId, currentDesignId)) {
        return;
      }

      return { ...state, currentDesignId };
    },
    setCurrentDesignName: (state, { payload: currentDesignName }: PayloadAction<Account['currentDesignName']>) => {
      if (isShallowIdentical(current(state).currentDesignName, currentDesignName)) {
        return;
      }

      return { ...state, currentDesignName };
    },
    setLoggedIn: (state, { payload: loggedIn }: PayloadAction<Account['loggedIn']>) => {
      if (isShallowIdentical(current(state).loggedIn, loggedIn)) {
        return;
      }

      return { ...state, loggedIn };
    },
    setVisibleModal: (state, { payload: visibleModal }: PayloadAction<Account['visibleModal']>) => {
      if (isShallowIdentical(current(state).visibleModal, visibleModal)) {
        return;
      }

      return { ...state, visibleModal };
    },
    setDesigns: (state, { payload: designs }: PayloadAction<Account['designs']>) => {
      if (isShallowIdentical(current(state).designs, designs)) {
        return;
      }

      return { ...state, designs };
    },
    setSaveDesignState: (state, { payload: saveDesignState }: PayloadAction<Account['saveDesignState']>) => {
      if (isShallowIdentical(current(state).saveDesignState, saveDesignState)) {
        return;
      }

      return { ...state, saveDesignState };
    },
  },
});
